import { Dao } from '../../dao';
import { Gizmo } from '../../gizmos';
import { IGizmoCallbacks, IGizmoData } from '../../types';
import { gnov } from '../../utils';
import selectMultipleValues from './selectMultipleValues';
import selectFieldValues from './select-field-values';
import annexFormValues from './annex-form-values';
import accessMetadata from './access-metadata';
import parentData from './parent-data';

interface IInfluxParams {
  variable: string;
  context: {
    //! Should we remove this? Since those are carried inside the Gizmo
    gizmoCallbacks: IGizmoCallbacks;
    dao?: Dao;
    gizmo?: Gizmo;
  };
}

function influx({ variable, context }: IInfluxParams): Promise<any> {
  return new Promise<any>(async resolve => {
    const { dao, gizmo, gizmoCallbacks } = context;

    if (variable.startsWith('user.') && dao) {
      // Right now it's only prepared to work with first level keys in the user
      let key = variable.split('.')[1];

      // If the key is a fid we're going to complement it with more info
      if (key.match(/^\s*q_?\d*\s*/g)) {
        key = `variables.${key}.v`;
      }

      const user = dao.getUser();
      const value = gnov(user, key);

      return resolve(value);
    } else if (variable.startsWith('flowy.')) {
      if (variable === 'flowy.mode') {
        const value = gizmoCallbacks.getInfoCB({
          type: 'param',
          path: 'mode',
        });

        if (value) {
          resolve(value);
        } else {
          // By default the 'flowy.mode' is 'edit'
          resolve('edit');
        }
      }
    } else if (variable.startsWith('index') && gizmo) {
      const [uid, accessor] = variable.split('.');
      const childIndex = gizmo.getParams()['child-index'];
      if (uid && accessor === undefined && childIndex !== undefined) {
        return resolve(childIndex);
      } else if (uid && accessor === 'first' && childIndex !== undefined) {
        return resolve(childIndex === 0);
      }
    } else if (variable.startsWith('parent')) {
      return resolve(
        parentData({
          variable,
          context: {
            gizmo,
          },
        })
      );
    } else if (variable === 'status') {
      const value = gizmoCallbacks.getInfoCB({
        type: 'rootGizmoDataStatus',
      });

      return resolve(value);
    } else if (variable.match(/^\s*q_?\d*\s*/g)) {
      // This means we've specified a fid
      const value = gizmoCallbacks.getInfoCB({
        type: 'data',
        fid: variable,
      });

      if (value !== undefined && value.v !== undefined) {
        return resolve(value.v);
      }
    } else if (variable === 'value' && gizmo) {
      const data = gizmo.getData();
      if (data && !Array.isArray(data) && data.v !== undefined) {
        return resolve(data.v);
      }
    } else {
      const [uid] = variable.split('.');
      // This means we've specifieda uid
      const value: IGizmoData = gizmoCallbacks.getInfoCB({
        type: 'data',
        uid,
      });
      const answerGizmo: Gizmo | undefined = gizmoCallbacks.getInfoCB({
        type: 'gizmo',
        uid,
      });

      if (
        answerGizmo?.getConfig().type === 'annex_form' &&
        variable.split('.').length > 1
      ) {
        return resolve(
          annexFormValues({
            variable,
            context: {
              answerGizmo,
              ...context,
            },
          })
        );
      } else if (
        answerGizmo?.getConfig().type === 'select_field' &&
        answerGizmo?.getConfig().ops?.selectField?.isMultiple &&
        Array.isArray(value)
      ) {
        return resolve(
          selectMultipleValues({
            variable,
            data: value,
            context: {
              answerGizmo,
            },
          })
        );
      } else if (answerGizmo?.getConfig().type === 'select_field') {
        // This will happen when we have a select_field and we're using accesor operator
        // Right now it basically has support to access the option metadata
        return resolve(
          selectFieldValues({
            variable,
            data: value,
            context: {
              answerGizmo,
            },
          })
        );
      } else if (
        value !== undefined &&
        value.v !== undefined &&
        variable.split('.').length > 1
      ) {
        return resolve(
          accessMetadata({
            variable,
            data: value,
            context: {
              answerGizmo,
            },
          })
        );
      } else if (value !== undefined && value.v !== undefined) {
        return resolve(value.v);
      } else if (answerGizmo?.getConfig().type === 'number_field') {
        // If we have a number field without answer we're going to return a 0
        return resolve(0);
      }
    }
    resolve(undefined);
  });
}

export default influx;
